<template>
  <div :class="$style.leadCharge">
    <!-- 顶部导航 -->
    <div :class="$style.header">
      <span>支付</span>
    </div>

    <!-- 页面加载中状态 -->
    <div :class="$style.pageLoading" v-if="pageLoading">
      <van-loading type="spinner" color="#ff4d4f" />
      <p>正在查询订单状态...</p>
    </div>

    <!-- 页面不可用状态 -->
    <div :class="$style.unavailable" v-if="orderInfo.status === 0">
      <p>当前订单已失效</p>
    </div>

    <!-- 未支付状态 - 订单信息和支付方式选择 -->
    <div :class="$style.payPage" v-else-if="orderInfo.pay_status === 0">
      <!-- 支付金额 -->
      <div :class="$style.amountSection">
        <p :class="$style.amountLabel">支付金额</p>
        <p :class="$style.amountValue">¥{{ formatMoney(orderInfo.money) }}</p>
        <!-- 宣传语 -->
        <p :class="$style.slogan" v-if="orderInfo.product_name">支付成功豪礼大放送</p>
      </div>

      <!-- 订单信息 -->
      <div :class="$style.oorderInfo">
        <div :class="$style.infoItem">
          <span :class="$style.label">游戏：</span>
          <span :class="$style.value">{{ orderInfo.game }}</span>
        </div>
        <div :class="$style.infoItem">
          <span :class="$style.label">区服：</span>
          <span :class="[$style.value, $style.highlight]">{{ orderInfo.server_name }}</span>
        </div>
        <div :class="$style.infoItem">
          <span :class="$style.label">角色：</span>
          <span :class="$style.value">{{ orderInfo.role_name }}</span>
        </div>
        <div :class="$style.infoItem">
          <span :class="$style.label">购买道具：</span>
          <span :class="[$style.value, $style.productName]">{{ orderInfo.product_name }}</span>
        </div>
      </div>

      <!-- 支付方式选择 - 仅在非微信浏览器中显示 -->
      <div :class="$style.payTypeSection" v-if="!isInWechat">
        <p :class="$style.sectionTitle">请选择支付方式</p>
        <RadioGroup v-model="selectedPayType">
          <div :class="$style.payTypeList">
            <div :class="$style.payTypeItem" @click="selectedPayType = 'alipay'">
              <div :class="$style.payTypeLeft">
                <img :src="`${$cdnUrl}/offlinePay/drawable-xxhdpi/zhifubao.png`" alt="支付宝" />
                <span>支付宝</span>
              </div>
              <Radio name="alipay" checked-color="#FF6600"></Radio>
            </div>
            <div :class="$style.payTypeItem" @click="selectedPayType = 'wechat'">
              <div :class="$style.payTypeLeft">
                <img :src="`${$cdnUrl}/offlinePay/drawable-xxhdpi/weixin.png`" alt="微信" />
                <span>微信</span>
              </div>
              <Radio name="wechat" checked-color="#FF6600"></Radio>
            </div>
          </div>
        </RadioGroup>
      </div>

      <!-- 微信内提示 -->
      <div :class="$style.wechatTip" v-else>
        <p>将使用微信支付</p>
      </div>

      <!-- 立即支付按钮 -->
      <Button :class="$style.payBtn" @click="handlePay" :loading="payLoading">立即支付</Button>
    </div>

    <!-- 已支付状态 - 礼包码展示 -->
    <div :class="$style.giftPage" v-else-if="orderInfo.pay_status === 1">
      <!-- 礼包码领取成功 -->
      <template v-if="orderInfo.gift_status === 2 && orderInfo.gift_code">
        <div :class="$style.giftSuccess">
          <p :class="$style.successTitle">恭喜您成功领取福利礼包！</p>
          <p :class="$style.giftCodeLabel">您的礼包码为：<span :class="$style.giftCode">{{ orderInfo.gift_code }}</span></p>
          <p :class="$style.giftTip">您可点击下方"复制礼包码"，前往游戏中的礼包兑换入口，输入后即可成功领取</p>
          <!-- 礼包剩余时间提示 -->
          <p :class="$style.remainTip" v-if="remainMinutes > 0">{{ formattedRemainTime }}后失效，请及时兑换或保存</p>
        </div>
        <Button :class="$style.copyBtn" @click="copyGiftCode">复制礼包码</Button>
        <p :class="$style.contactService">如有任何疑问，请<a :href="serviceUrl" target="_blank" @click="handleCustomerServiceClick">联系客服</a></p>
      </template>

      <!-- 支付结果查询中 -->
      <template v-else-if="orderInfo.gift_status === 1">
        <div :class="$style.giftLoading">
          <van-loading type="spinner" color="#ff4d4f" />
          <p>支付结果查询中，请稍候...</p>
        </div>
      </template>

      <!-- 礼包码领取失败 -->
      <template v-else-if="orderInfo.gift_status === 3">
        <div :class="$style.giftFailed">
          <p :class="$style.failTitle">礼包码领取失败</p>
          <p :class="$style.failTip">请联系客服处理</p>
        </div>
        <p :class="$style.contactService">如有任何疑问，请<a :href="serviceUrl" target="_blank" @click="handleCustomerServiceClick">联系客服</a></p>
      </template>

      <!-- 无礼包码 - 普通支付成功 -->
      <template v-else-if="orderInfo.gift_status === 0">
        <div :class="$style.paySuccess">
          <img :src="`${$cdnUrl}/success.png`" alt="成功" :class="$style.successIcon" />
          <p :class="$style.successText">支付成功</p>
          <p :class="$style.successTip">请到游戏内查询具体结果</p>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { Button, RadioGroup, Radio, Toast, Loading } from 'vant';
import { getLeadOfflineOrderInfo, leadOfflinePay } from '@/api/pay';
import { isWeiXin, getCurrentAppkey } from '@/libs/utils';
import wxSdkInit from '@/libs/wx.init.js';
import { QI_DIAN_URL } from '@/config';
import leadChargeTrackMixin from './leadChargeTrack.js';

export default {
  name: 'LeadCharge',
  mixins: [leadChargeTrackMixin],
  components: {
    Button,
    RadioGroup,
    Radio,
    [Loading.name]: Loading
  },
  data() {
    return {
      orderInfo: {
        money: 0,
        game: '',
        server_name: '',
        role_name: '',
        product_name: '',
        pay_status: 0,
        gift_status: 0,
        gift_code: '',
        remain_minute: 0, // 礼包剩余有效时间（分钟）
        status: 1
      },
      selectedPayType: 'wechat', // 默认选择微信支付
      payLoading: false,
      pollingTimer: null,
      serviceUrl: QI_DIAN_URL,
      pageLoading: false,
      remainMinutes: 0,
      countdownTimer: null,
      isPolling: false // 是否已重试过获取礼包码状态
    };
  },
  computed: {
    // 是否在微信内
    isInWechat() {
      return isWeiXin();
    },
    // 获取微信授权 appId
    wxAppId() {
      return this.$route.query.wx_app_id || this.$route.meta.auth;
    },
    // 根据域名判断使用哪个 appkey
    currentAppkey() {
      return getCurrentAppkey(this.$route);
    },
    // URL参数
    leadId() {
      return this.$route.query.lead_id;
    },
    orderToken() {
      return this.$route.query.order_token;
    },
    // 支付类型：微信内52公众号，浏览器26微信官方/57支付宝
    currentPayType() {
      if (this.isInWechat) return 52;
      return this.selectedPayType === 'wechat' ? 26 : 57;
    },
    // 是否支付同步返回（需要延迟请求）
    isSyncReturn() {
      return this.$route.query.is_sync_return === '1';
    },
    // 格式化剩余时间（天小时分钟）
    formattedRemainTime() {
      if (this.remainMinutes <= 0) return '';
      const days = Math.floor(this.remainMinutes / 1440);
      const hours = Math.floor((this.remainMinutes % 1440) / 60);
      const minutes = this.remainMinutes % 60;
      
      let result = '';
      if (days > 0) result += `${days}天`;
      if (hours > 0) result += `${hours}小时`;
      if (minutes > 0 || result === '') result += `${minutes}分钟`;
      return result;
    }
  },
  created() {
    document.title = '支付';
  },
  mounted() {
    // 微信内初始化SDK
    if (this.isInWechat) {
      wxSdkInit(this.currentAppkey, this.wxAppId);
    }
    // 支付同步返回时延迟2秒请求，等待异步通知完成
    if (this.isSyncReturn) {
      this.pageLoading = true;
      setTimeout(() => {
        this.getOrderInfo().finally(() => {
          this.pageLoading = false;
        });
      }, 2000);
    } else {
      // 获取订单信息
      this.getOrderInfo();
    }
  },
  beforeDestroy() {
    // 清除轮询定时器
    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
    }
    this.isPolling = false; // 重置重试标志
    // 清除倒计时定时器
    this.stopCountdown();
  },
  methods: {
    // 格式化金额（分转元）
    formatMoney(money) {
      if (!money) return '0.00';
      return (money / 100).toFixed(2);
    },
    // 订单接口的状态字段可能是字符串，统一转数字后再做严格判断
    normalizeOrderInfo(orderInfo = {}) {
      const normalized = Object.assign({}, orderInfo);
      ['pay_status', 'gift_status', 'status', 'remain_minute', 'money'].forEach(key => {
        if (normalized[key] !== undefined && normalized[key] !== null && normalized[key] !== '') {
          normalized[key] = Number(normalized[key]);
        }
      });
      return normalized;
    },
    // 获取订单信息
    async getOrderInfo() {
      // 参数缺失时显示页面不可用
      if (!this.leadId || !this.orderToken) {
        this.orderInfo.status = 0;
        return;
      }
      try {
        const res = await getLeadOfflineOrderInfo({
          lead_id: this.leadId,
          order_token: this.orderToken
        });
        this.orderInfo = this.normalizeOrderInfo(res.content);
        this.reportExpose();
        this.tryReportGiftCodePageView();
        
        // 如果礼包码状态为领取中，且未重试过，2秒后重试一次
        if (this.orderInfo.pay_status === 1 && this.orderInfo.gift_status === 1 && !this.isPolling) {
          this.retryOnce();
        }
        
        // 如果有剩余时间，启动倒计时
        if (this.orderInfo.remain_minute > 0) {
          this.remainMinutes = this.orderInfo.remain_minute;
          this.startCountdown();
        }
      } catch (error) {
        // 接口失败时显示页面不可用
        this.orderInfo.status = 0;
        Toast(error.msg || '获取订单信息失败');
      }
    },
    // 2秒后重试一次获取订单信息，如果仍是领取中则按未领取处理
    retryOnce() {
      this.isPolling = true; // 标记已重试，防止重复触发
      this.pollingTimer = setTimeout(async () => {
        try {
          const res = await getLeadOfflineOrderInfo({
            lead_id: this.leadId,
            order_token: this.orderToken
          });
          this.orderInfo = this.normalizeOrderInfo(res.content);
          
          // 如果仍然是领取中状态，按未领取（普通支付成功）展示
          if (this.orderInfo.gift_status === 1) {
            this.orderInfo.gift_status = 0;
          }
          this.tryReportGiftCodePageView();
          
          // 如果有剩余时间，启动倒计时
          if (this.orderInfo.remain_minute > 0) {
            this.remainMinutes = this.orderInfo.remain_minute;
            this.startCountdown();
          }
        } catch (error) {
          // 重试失败，按支付成功展示
          this.orderInfo.gift_status = 0;
        }
      }, 2000);
    },
    // 处理支付
    async handlePay() {
      if (this.payLoading) return;
      this.payLoading = true;
      this.reportConfirmClick();

      try {
        const params = {
          lead_id: this.leadId,
          order_token: this.orderToken,
          pay_type: this.currentPayType
        };

        // 微信内需要传openid和wx_app_id
        if (this.isInWechat) {
          const userInfo = JSON.parse(localStorage.getItem('userInfo'));
          if (userInfo && userInfo.open_id) {
            params.openid = userInfo.open_id;
            params.wx_app_id = this.wxAppId;
          }
        }

        const res = await leadOfflinePay(params);

        if (this.isInWechat) {
          // 使用pay_data调起JSAPI支付
          this.invokeWxPay(res.content.pay_data);
        } else {
          // 跳转pay_url
          this.isPayRedirecting = true;
          window.location.href = res.content.pay_url;
        }
      } catch (error) {
        Toast(error.msg || '支付失败');
      } finally {
        this.payLoading = false;
      }
    },
    // 调起微信JSAPI支付
    invokeWxPay(payData) {
      if (!payData) {
        Toast('支付参数错误');
        return;
      }
      const { nonceStr, paySign, timeStamp, signType } = payData;
      wx.chooseWXPay({
        timestamp: timeStamp,
        nonceStr,
        package: payData.package,
        signType,
        paySign,
        success: () => {
          this.refreshOrderInfo();
        },
        fail: () => {
          this.refreshOrderInfo();
        },
        cancel: () => {
          this.refreshOrderInfo();
        }
      });
    },
    // 刷新订单信息
    async refreshOrderInfo() {
      await this.getOrderInfo();
      if (this.orderInfo.pay_status === 1) {
        Toast('支付成功');
      } else {
        Toast('支付未完成，请重试');
      }
    },
    // 复制礼包码
    async copyGiftCode() {
      this.giftCodeCopyClickCount += 1;
      this.reportGiftCodeCopyClick();
      const code = this.orderInfo.gift_code;
      if (!code) {
        Toast('礼包码不存在');
        return;
      }
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(code);
          Toast('复制成功');
        } else {
          // 降级方案
          const textArea = document.createElement('textarea');
          textArea.value = code;
          textArea.style.position = 'fixed';
          textArea.style.left = '-9999px';
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          Toast('复制成功');
        }
      } catch (error) {
        Toast('复制失败，请手动复制');
      }
    },
    // 启动倒计时（每分钟递减）
    startCountdown() {
      this.stopCountdown(); // 先清除之前的定时器
      this.countdownTimer = setInterval(() => {
        if (this.remainMinutes > 0) {
          this.remainMinutes--;
        } else {
          this.stopCountdown();
        }
      }, 60000);
    },
    // 停止倒计时
    stopCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    }
  }
};
</script>

<style lang="scss" module>
.leadCharge {
  min-height: 100vh;
  background: #f6f6f6;
  padding-bottom: 30px;
}

.header {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  background: #fff;
  font-size: 18px;
  font-weight: 500;
  position: relative;
  border-bottom: 1px solid #eee;

  :global {
    .iconfont {
      position: absolute;
      left: 15px;
      font-size: 20px;
      color: #333;
    }
  }
}

.unavailable {
  padding: 100px 20px;
  text-align: center;
  
  p {
    font-size: 16px;
    color: #999;
  }
}

.pageLoading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 20px;
  
  p {
    margin-top: 15px;
    font-size: 14px;
    color: #666;
  }
}

.payPage {
  padding: 0 15px;
}

.amountSection {
  background: #fff;
  border-radius: 8px;
  padding: 25px 15px;
  margin-top: 15px;
  text-align: center;
}

.amountLabel {
  font-size: 14px;
  color: #666;
  margin-bottom: 10px;
}

.amountValue {
  font-size: 36px;
  font-weight: bold;
  color: #333;
}

.slogan {
  font-size: 16px;
  color: #ff4d4f;
  font-weight: 500;
  margin-top: 15px;
}

.orderInfo {
  background: #fff;
  border-radius: 8px;
  padding: 15px;
  margin-top: 15px;
}

.infoItem {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }
}

.label {
  font-size: 14px;
  color: #666;
}

.value {
  font-size: 14px;
  color: #333;
}

.highlight {
  color: #1890ff;
}

.productName {
  color: #ff6600;
}

.payTypeSection {
  background: #fff;
  border-radius: 8px;
  padding: 15px;
  margin-top: 15px;
}

.sectionTitle {
  font-size: 14px;
  color: #333;
  font-weight: 500;
  margin-bottom: 15px;
}

.payTypeList {
  :global {
    .van-radio-group {
      width: 100%;
    }
  }
}

.payTypeItem {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }
}

.payTypeLeft {
  display: flex;
  align-items: center;

  img {
    width: 24px;
    height: 24px;
    margin-right: 10px;
  }

  span {
    font-size: 14px;
    color: #333;
  }
}

.wechatTip {
  background: #fff;
  border-radius: 8px;
  padding: 15px;
  margin-top: 15px;
  text-align: center;

  p {
    font-size: 14px;
    color: #666;
  }
}

.payBtn {
  display: block;
  width: 80%;
  height: 46px;
  margin: 30px auto 0;
  background: #ff4d4f;
  border: none;
  border-radius: 23px;
  font-size: 18px;
  color: #fff;
}

.giftPage {
  padding: 30px 20px;
}

.giftSuccess {
  text-align: center;
  padding: 40px 15px;
  background: #fff;
  border-radius: 8px;
}

.successTitle {
  font-size: 18px;
  color: #ff4d4f;
  font-weight: 500;
  margin-bottom: 15px;
}

.giftCodeLabel {
  font-size: 16px;
  color: #ff4d4f;
  margin-bottom: 20px;
}

.giftCode {
  font-weight: bold;
}

.giftTip {
  font-size: 13px;
  color: #666;
  line-height: 1.6;
  padding: 0 10px;
}

.remainTip {
  font-size: 14px;
  color: #ff4d4f;
  margin-top: 15px;
  padding: 8px 15px;
  background: #fff5f5;
  border-radius: 4px;
  font-weight: 500;
}

.copyBtn {
  display: block;
  width: 60%;
  height: 44px;
  margin: 30px auto 0;
  background: #ff4d4f;
  border: none;
  border-radius: 22px;
  font-size: 16px;
  color: #fff;
}

.contactService {
  text-align: center;
  font-size: 13px;
  color: #666;
  margin-top: 20px;

  a {
    color: #ff4d4f;
    text-decoration: none;
  }
}

.giftLoading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: #fff;
  border-radius: 8px;

  p {
    margin-top: 15px;
    font-size: 16px;
    color: #666;
  }
}

.giftFailed {
  text-align: center;
  padding: 40px 20px;
  background: #fff;
  border-radius: 8px;
}

.failTitle {
  font-size: 18px;
  color: #ff4d4f;
  margin-bottom: 10px;
}

.failTip {
  font-size: 14px;
  color: #666;
}

.paySuccess {
  text-align: center;
  padding: 40px 20px;
  background: #fff;
  border-radius: 8px;
}

.successIcon {
  width: 60px;
  height: 60px;
  margin-bottom: 20px;
}

.successText {
  font-size: 20px;
  color: #52c41a;
  font-weight: 500;
  margin-bottom: 10px;
}

.successTip {
  font-size: 14px;
  color: #666;
}
</style>
